<template>
  <UITabContainer
    v-if="tabs.length"
    class="workflow-dashboard"
    :tabs="tabs"
    data-cy="workflow-dashboard"
  >
    <template v-slot:tabHeaderContent>
      <div class="workflow-dashboard-header">
        <label class="urgent-checkbox" for="is-urgent">URGENT</label>
        <input
          type="checkbox"
          id="is-urgent"
          v-model="isShowUrgent"
          @change="onUrgentClick"
        />
      </div>
    </template>
    <template v-slot:JobTab>
      <div class="dashboard-tabpanel">
        <span class="cc-label-name job-info-row">Job ID:</span>
        <span class="cc-label-value">{{ submission.Submission.JobID }}</span
        ><br />
        <span class="cc-label-name job-info-row">Company:</span
        ><span class="cc-label-value">{{
          submission.CompanyName + " (" + submission.CompanyId + ")"
        }}</span
        ><br />
        <span class="cc-label-name job-info-row">Assigned To:</span
        ><span class="cc-label-value">{{ submission.AssignedTo }}</span
        ><br />
        <span class="cc-label-name job-info-row">Task Status:</span>
        <span class="cc-label-value">{{
          EnumWorkflowStatus[submission.Status!]
        }}</span
        ><br />
        <span class="cc-label-name job-info-row">Submission Date:</span
        ><span class="cc-label-value">{{
          new Date(submission.SubmissionDate).toLocaleString()
        }}</span>
        <br />
        <span class="cc-label-name job-info-row">RC:</span>
        <span class="cc-label-value">{{ submission.CompanyPerson }}</span>
      </div>
    </template>
    <template v-slot:SubmissionTab>
      <div class="dashboard-tabpanel">
        <span class="cc-label-name job-info-row">Company:</span
        ><span class="cc-label-value">{{
          submission.CompanyName + " (" + submission.CompanyId + ")"
        }}</span
        ><br /><br />
        <div class="dashboard-tabpanel__row">
          <div class="dashboard-tabpanel__col">
            <span class="cc-label-name job-info-row">Company Status:</span
            ><span class="cc-label-value">{{
              EnumCompanyStatus[submission.CompanyStatus!]
            }}</span
            ><br />
            <span class="cc-label-name job-info-row">RC:</span
            ><span class="cc-label-value">{{ submission.CompanyPerson }}</span
            ><br />
            <span class="cc-label-name job-info-row">Support Staff:</span
            ><span class="cc-label-value">{{
              submission.AdministrativeAssistant
            }}</span
            ><br />
            <span class="cc-label-name job-info-row">Group Leader:</span
            ><span class="cc-label-value"
              >Rabbi {{ submission.GroupLeader }}</span
            >
          </div>
          <div class="dashboard-tabpanel__col">
            <span class="cc-label-name job-info-row">Submission ID:</span
            ><span class="cc-label-value">{{ submission.JobID }}</span
            ><br />
            <span class="cc-label-name job-info-row">Category:</span
            ><span class="cc-label-value">{{ submission.JobType }}</span
            ><br />
            <span class="cc-label-name job-info-row">Submitted By:</span
            ><span class="cc-label-value"
              >{{ submission.RequestedBy }} ({{
                submission.UserEmail?.split(";")[0]
              }})</span
            ><br />
            <span class="cc-label-name job-info-row">Line Item(s) Status:</span
            ><span class="cc-label-value">{{ submission.JobStatus }}</span
            ><br />
          </div>
        </div>
      </div>
    </template>
  </UITabContainer>
</template>

<script setup lang="ts">
import UITabContainer from "@/shared/ui/tabs/tab-container.vue";
import type { WfProductSubmissionDisplay } from "@/Domain/WfProductSubmissionDisplay";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { ref } from "vue";
import { useMainStore } from "@/stores/useMainStore";
import { EnumWorkflowStatus } from "@/Domain/EnumWorkflowStatus";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useNotificationStore } from "@/stores/useNotificationStore";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  submission: WfProductSubmissionDisplay;
  workflowId: string;
}>();

const emit = defineEmits<{
  (e: "urgentStatusChanged", urgent: boolean): void;
}>();

const isShowUrgent = ref<boolean>(props.submission.IsUrgent ?? false);

const tabs = [
  {
    id: "JobTab",
    label: "Job Info",
  },
  {
    id: "SubmissionTab",
    label: "Submission Info",
  },
];

const onUrgentClick = function () {
  const errorNotification = {
    message: `Error marking Job ID ${props.submission.JobID} as ${
      isShowUrgent.value ? "urgent" : "not urgent"
    }. `,
    type: NotificationTypeEnum.error,
    duration: 5000,
  };

  handleResponsePromiseAsync(
    api.get("SetWorkflowUrgentStatus", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      workflowId: parseInt(props.workflowId),
      isUrgent: isShowUrgent.value,
    }),
    (data) => {
      if (data) {
        notificationStore.notify({
          message: `Job ID ${props.submission.JobID} marked as ${
            isShowUrgent.value ? "urgent" : "not urgent"
          } `,
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
        emit("urgentStatusChanged", isShowUrgent.value);
      } else {
        notificationStore.notify(errorNotification);
      }
    },
    "WorkflowDetailIngredientsTable.vue",
    "onUrgentClick",
    () => {
      notificationStore.notify(errorNotification);
    }
  );
};
</script>

<style scoped lang="scss">
.workflow-dashboard {
  &-header {
    margin-left: auto;
    display: flex;
    align-items: center;
  }

  .dashboard-tabpanel {
    background: white;
    border: 1px solid #ddd;
    border-top: none;
    padding: 8px;

    &__row {
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;

      @include media-breakpoint-up(sm) {
        flex-direction: row;
      }
    }

    &__col {
      flex: 1;
      width: 100%;

      @include media-breakpoint-up(sm) {
        flex: 0 0 auto;
        width: 50%;
      }
    }
  }
}

.urgent-checkbox {
  color: #999;
  font-weight: bold;
  font-size: 12px;
}

.cc-label-value {
  //color: #999;
  font-weight: 200;
  font-size: 12px;
}
</style>
